<template>
  <div class="config-panel">
    <div class="panel-header">
      <div>
        <h3>Configure Transform</h3>
        <p>Map and transform your data columns</p>
      </div>
      <button @click="$emit('close')" class="btn-close">
        <X :size="20" />
      </button>
    </div>

    <div class="panel-body">
      <div class="info-card">
        <Zap :size="16" />
        <span>Select columns to include and map them to target names</span>
      </div>

      <!-- Column Mappings -->
      <div v-if="localMappings.length > 0" class="mappings-list">
        <div
          v-for="(mapping, index) in localMappings"
          :key="index"
          class="mapping-item"
        >
          <div class="mapping-header">
            <input
              type="checkbox"
              v-model="mapping.Include"
              :id="`mapping-${index}`"
            />
            <label :for="`mapping-${index}`">{{ mapping.SourceColumn }}</label>
          </div>

          <div v-if="mapping.Include" class="mapping-config">
            <input
              v-model="mapping.TargetColumn"
              placeholder="Target column name"
              class="form-input"
            />
            <span class="mapping-type">{{ mapping.DataType }}</span>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <AlertCircle :size="48" />
        <p>No columns available</p>
        <span>Configure the source node first</span>
      </div>
    </div>

    <div class="panel-footer">
      <button @click="$emit('close')" class="btn btn-secondary">
        Cancel
      </button>
      <button 
        @click="saveConfig" 
        class="btn btn-primary"
        :disabled="!isValid"
      >
        Apply Changes
      </button>
    </div>
  </div>
</template>

<script setup>
// @ts-nocheck - Will be properly typed later
import { ref, computed, watch, onMounted } from 'vue'
import { X, Zap, AlertCircle } from 'lucide-vue-next'

const props = defineProps({
  node: {
    type: Object,
    required: true
  },
  sourceNode: {  
    type: Object,
    default: null
  }
})


const emit = defineEmits(['update', 'close'])

// Initialize with sample data or existing mappings
const localMappings = ref([])

onMounted(() => {
  if (props.node.data.columnMappings?.length > 0) {
    // Use existing mappings if available
    localMappings.value = [...props.node.data.columnMappings]
  } else if (props.sourceNode?.data?.sampleColumns?.length > 0) {
    // Create mappings from source node columns - using PascalCase to match backend
    localMappings.value = props.sourceNode.data.sampleColumns.map(col => ({
      SourceColumn: col.name || col,
      TargetColumn: col.name || col,
      DataType: col.type || 'string',
      Include: true
    }))
  }
})



const isValid = computed(() => {
  return localMappings.value.some(m => m.Include)
})

watch(() => props.node.data.columnMappings, (newMappings) => {
  if (newMappings && newMappings.length > 0) {
    localMappings.value = newMappings
  }
}, { deep: true })

const saveConfig = () => {
  emit('update', {
    columnMappings: localMappings.value.filter(m => m.Include),
    label: `Transform (${localMappings.value.filter(m => m.Include).length} columns)`
  })
  emit('close')
}
</script>

<style scoped>
.config-panel {
  position: fixed;
  top: 78px;
  right: 0;
  width: 450px;
  height: calc(100vh - 78px);
  background: white;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 100;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.panel-header h3 {
  margin: 0 0 0.25rem 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.panel-header p {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-secondary);
}

.btn-close {
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--color-text-primary);
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(245, 158, 11, 0.05);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  font-size: 13px;
  color: #f59e0b;
}

.mappings-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mapping-item {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s;
}

.mapping-item:hover {
  border-color: var(--color-accent);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.mapping-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.mapping-header input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.mapping-header label {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  cursor: pointer;
}

.mapping-config {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.form-input {
  flex: 1;
  padding: 0.625rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  font-size: 13px;
}

.form-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.mapping-type {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text-secondary);
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-text-secondary);
}

.empty-state svg {
  color: var(--color-text-secondary);
  opacity: 0.5;
  margin-bottom: 1rem;
}

.empty-state p {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: var(--color-text-primary);
}

.empty-state span {
  font-size: 13px;
}

.panel-footer {
  padding: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.02);
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background: transparent;
  color: var(--color-text-secondary);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.btn-secondary:hover {
  background: rgba(0, 0, 0, 0.05);
}

.btn-primary {
  background: linear-gradient(135deg, #f59e0b, #ea580c);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>